import {
  getRecord, updateRecord, archiveRecord, restoreRecord, hardDeleteRecord,
  getHistory, getAllRecords, getWeights,
} from '../db/indexeddb';
import {
  DIMENSIONS, RECORD_TYPES, STATUSES, STATUS_LABELS, TYPE_LABELS, scoresOf,
  type GvmRecord, type Dimension,
} from '../lib/types';
import { escapeHtml, formatDate, formatDateTime, showToast, badge } from './shared';

const DIMENSION_LABELS: Record<Dimension, string> = {
  vision_alignment: 'Vision alignment',
  impact: 'Impact',
  revenue_potential: 'Revenue potential',
  market_window: 'Market window',
  time_efficiency: 'Time / resource efficiency',
  leverage: 'Leverage / compounding',
  evidence_readiness: 'Evidence / readiness',
};

export async function renderRecordDetail(container: HTMLElement, id: string, navigate: (hash: string) => void): Promise<void> {
  const record = await getRecord(id);
  if (!record) {
    container.innerHTML = `<div class="empty">Record ${escapeHtml(id)} not found.</div>`;
    return;
  }
  const [all, history, weights] = await Promise.all([getAllRecords(true), getHistory(id), getWeights()]);
  const byId = new Map(all.map((r) => [r.id, r]));
  const parent = record.parent_id ? byId.get(record.parent_id) : undefined;
  const related = record.related_ids.map((rid) => byId.get(rid)).filter(Boolean) as GvmRecord[];
  const children = all.filter((r) => r.parent_id === record.id);

  let editing = false;

  function renderView() {
    const scores = scoresOf(record!);
    container.innerHTML = `
      <a href="#/map" style="font-size:13px; color:var(--text-faint); text-decoration:none;">← Vision Map</a>

      <div class="overall-score-banner" style="margin-top:10px;">
        <div>
          <div style="font-size:12px; color:var(--text-faint);">Overall score</div>
          <div class="big">${record!.overall_score.toFixed(1)}<span style="font-size:16px; color:var(--text-faint);">/10</span></div>
        </div>
        <div style="text-align:right;">
          ${badge(STATUS_LABELS[record!.status])}
          ${record!.priority_rank != null ? `<div style="margin-top:6px; font-size:12px; color:var(--text-faint);">Manual rank #${record!.priority_rank}</div>` : ''}
        </div>
      </div>

      <h2 style="font-size:19px;">${escapeHtml(record!.title)}</h2>
      <div style="font-size:12px; color:var(--text-faint); margin:2px 0 12px;">${record!.id} · ${TYPE_LABELS[record!.type]}</div>
      ${record!.description ? `<p style="color:var(--text-dim); font-size:14px; margin-bottom:14px;">${escapeHtml(record!.description)}</p>` : ''}

      <div class="section-title">Why it has this rank</div>
      <div class="card">
        ${DIMENSIONS.map((d) => {
          const s = scores[d];
          const w = weights[d];
          const contrib = s * w;
          return `<div class="score-row">
            <div class="score-label">${DIMENSION_LABELS[d]} <span style="color:var(--text-faint);">(${Math.round(w * 100)}%)</span></div>
            <div style="width:90px; text-align:right; font-variant-numeric: tabular-nums;">${s}/10 → <b>${contrib.toFixed(2)}</b></div>
          </div>`;
        }).join('')}
      </div>

      <div class="section-title">Execution</div>
      <div class="card">
        ${fieldRow('Owner', record!.owner || '—')}
        ${fieldRow('Execution lane', record!.execution_lane || '—')}
        ${fieldRow('Next action', record!.next_action || '—')}
        ${fieldRow('Activation gate', record!.activation_gate || '—')}
        ${fieldRow('Current blocker', record!.current_blocker || '—', record!.current_blocker ? 'color:var(--status-blocked);' : '')}
        ${fieldRow('Reactivation trigger', record!.reactivation_trigger || '—')}
        ${fieldRow('Deadline', record!.deadline ? formatDate(record!.deadline) : '—')}
        ${record!.execution_allocation_pct != null ? fieldRow('Execution allocation', `${record!.execution_allocation_pct}%`) : ''}
      </div>

      <div class="section-title">Relationships</div>
      <div class="card">
        ${fieldRow('Parent', parent ? `<a href="#/record/${parent.id}">${escapeHtml(parent.title)}</a>` : '—')}
        ${fieldRow('Related', related.length ? related.map((r) => `<a href="#/record/${r.id}">${escapeHtml(r.title)}</a>`).join(', ') : '—')}
        ${fieldRow('Sub-records', children.length ? children.map((r) => `<a href="#/record/${r.id}">${escapeHtml(r.title)}</a>`).join(', ') : '—')}
      </div>

      <div class="section-title">Notes</div>
      <div class="card">${record!.notes ? escapeHtml(record!.notes).replace(/\n/g, '<br>') : '<span style="color:var(--text-faint);">No notes.</span>'}</div>

      <div class="section-title">Record</div>
      <div class="card">
        ${fieldRow('Category', record!.category || '—')}
        ${fieldRow('Created', formatDateTime(record!.created_at))}
        ${fieldRow('Updated', formatDateTime(record!.updated_at))}
        ${fieldRow('Last reviewed', record!.last_reviewed_at ? formatDateTime(record!.last_reviewed_at) : 'Never')}
      </div>

      <div class="section-title">Activity / History
        <span style="font-size:11px; color:var(--text-faint); text-transform:none; letter-spacing:0;">${history.length} entries</span>
      </div>
      <div class="card">
        ${history.length ? history.slice(0, 25).map((h) => `
          <div class="history-item">
            <div class="when">${formatDateTime(h.timestamp)}</div>
            <div>${h.field ? `<b>${escapeHtml(h.field)}</b>: ${escapeHtml(h.old_value ?? '—')} → ${escapeHtml(h.new_value ?? '—')}` : ''}${h.note ? `${h.field ? ' — ' : ''}${escapeHtml(h.note)}` : ''}</div>
          </div>
        `).join('') : '<div class="empty">No history yet.</div>'}
      </div>

      <div class="form-actions" style="position:static; margin-top:20px;">
        <button class="primary" id="rd-edit">Edit</button>
        ${record!.archived
          ? '<button id="rd-restore">Restore</button>'
          : '<button id="rd-archive">Archive</button>'}
      </div>
      <div style="margin-top:10px;">
        <button class="danger" id="rd-delete" style="width:100%;">Permanently Delete</button>
      </div>
    `;

    container.querySelector('#rd-edit')?.addEventListener('click', () => { editing = true; renderEdit(); });
    container.querySelector('#rd-archive')?.addEventListener('click', async () => {
      if (!window.confirm(`Archive "${record!.title}"? It will leave active views but can be restored anytime.`)) return;
      await archiveRecord(record!.id);
      showToast('Archived');
      navigate('#/map');
    });
    container.querySelector('#rd-restore')?.addEventListener('click', async () => {
      await restoreRecord(record!.id);
      record!.archived = false;
      showToast('Restored');
      renderView();
    });
    container.querySelector('#rd-delete')?.addEventListener('click', async () => {
      const first = window.confirm(
        `Permanently delete "${record!.title}"? This cannot be undone and removes it (and its history) from this device.`,
      );
      if (!first) return;
      const second = window.prompt(`This is permanent. Type the record's ID to confirm: ${record!.id}`);
      if (second !== record!.id) { showToast('Delete cancelled — ID did not match', 'error'); return; }
      await hardDeleteRecord(record!.id);
      showToast('Permanently deleted');
      navigate('#/map');
    });
  }

  function renderEdit() {
    const scores = scoresOf(record!);
    container.innerHTML = `
      <a href="#" id="rd-cancel-top" style="font-size:13px; color:var(--text-faint); text-decoration:none;">← Cancel</a>
      <h2 style="font-size:19px; margin-top:10px;">Edit: ${escapeHtml(record!.title)}</h2>

      <label class="required">Title</label>
      <input id="e-title" value="${escapeHtml(record!.title)}" />

      <label>Description</label>
      <textarea id="e-desc">${escapeHtml(record!.description)}</textarea>

      <label>Type</label>
      <select id="e-type">${RECORD_TYPES.map((t) => `<option value="${t}" ${t === record!.type ? 'selected' : ''}>${TYPE_LABELS[t]}</option>`).join('')}</select>

      <label>Status</label>
      <select id="e-status">${STATUSES.map((s) => `<option value="${s}" ${s === record!.status ? 'selected' : ''}>${STATUS_LABELS[s]}</option>`).join('')}</select>

      <label>Category</label>
      <input id="e-category" value="${escapeHtml(record!.category)}" />

      <label>Owner</label>
      <input id="e-owner" value="${escapeHtml(record!.owner)}" />

      <label>Execution lane</label>
      <input id="e-lane" value="${escapeHtml(record!.execution_lane)}" />

      <label>Priority rank <span style="color:var(--text-faint); font-weight:400;">(manual, optional)</span></label>
      <input id="e-rank" type="number" min="1" value="${record!.priority_rank ?? ''}" />

      <label>Execution allocation % <span style="color:var(--text-faint); font-weight:400;">(optional)</span></label>
      <input id="e-alloc" type="number" min="0" max="100" value="${record!.execution_allocation_pct ?? ''}" />

      <label>Next action</label>
      <input id="e-next" value="${escapeHtml(record!.next_action)}" />

      <label>Activation gate</label>
      <input id="e-gate" value="${escapeHtml(record!.activation_gate)}" />

      <label>Current blocker</label>
      <input id="e-blocker" value="${escapeHtml(record!.current_blocker)}" />

      <label>Reactivation trigger</label>
      <input id="e-trigger" value="${escapeHtml(record!.reactivation_trigger)}" />

      <label>Deadline</label>
      <input id="e-deadline" type="date" value="${record!.deadline ?? ''}" />

      <label>Notes</label>
      <textarea id="e-notes">${escapeHtml(record!.notes)}</textarea>

      <div class="section-title">Scores (1–10)</div>
      <div class="card">
        ${DIMENSIONS.map((d) => `
          <div class="score-row">
            <div class="score-label">${DIMENSION_LABELS[d]}</div>
            <input type="range" min="1" max="10" step="1" value="${scores[d]}" id="e-score-${d}" data-dim="${d}" />
            <div class="score-value" id="e-score-${d}-val">${scores[d]}</div>
          </div>
        `).join('')}
        <div style="margin-top:10px; font-size:13px; color:var(--text-dim);">Live overall: <b id="e-live-score" style="color:var(--accent);">${record!.overall_score.toFixed(1)}</b></div>
      </div>

      <label>Reviewed just now? <span style="color:var(--text-faint); font-weight:400;">(marks last-reviewed timestamp)</span></label>
      <input type="checkbox" id="e-reviewed" style="width:auto; min-height:auto;" />

      <label>Edit note <span style="color:var(--text-faint); font-weight:400;">(optional, logged to history)</span></label>
      <input id="e-note" placeholder="Why this change?" />

      <div class="form-actions">
        <button class="ghost" id="rd-cancel">Cancel</button>
        <button class="primary" id="rd-save">Save</button>
      </div>
    `;

    const liveScoreEl = container.querySelector('#e-live-score') as HTMLElement;
    DIMENSIONS.forEach((d) => {
      const input = container.querySelector(`#e-score-${d}`) as HTMLInputElement;
      const valEl = container.querySelector(`#e-score-${d}-val`) as HTMLElement;
      input.addEventListener('input', () => {
        valEl.textContent = input.value;
        const live: Record<string, number> = {};
        DIMENSIONS.forEach((dd) => {
          live[dd] = dd === d ? Number(input.value) : Number((container.querySelector(`#e-score-${dd}`) as HTMLInputElement).value);
        });
        const total = DIMENSIONS.reduce((sum, dd) => sum + live[dd] * weights[dd], 0);
        liveScoreEl.textContent = total.toFixed(2);
      });
    });

    const cancel = () => { editing = false; renderView(); };
    container.querySelector('#rd-cancel')?.addEventListener('click', cancel);
    container.querySelector('#rd-cancel-top')?.addEventListener('click', (e) => { e.preventDefault(); cancel(); });

    container.querySelector('#rd-save')?.addEventListener('click', async () => {
      const g = (sel: string) => (container.querySelector(sel) as HTMLInputElement).value;
      const title = g('#e-title').trim();
      if (!title) { showToast('Title is required', 'error'); return; }

      const patch: any = {
        title,
        description: g('#e-desc'),
        type: g('#e-type'),
        status: g('#e-status'),
        category: g('#e-category'),
        owner: g('#e-owner'),
        execution_lane: g('#e-lane'),
        priority_rank: g('#e-rank') ? Number(g('#e-rank')) : null,
        execution_allocation_pct: g('#e-alloc') ? Number(g('#e-alloc')) : null,
        next_action: g('#e-next'),
        activation_gate: g('#e-gate'),
        current_blocker: g('#e-blocker'),
        reactivation_trigger: g('#e-trigger'),
        deadline: g('#e-deadline') || null,
        notes: g('#e-notes'),
        note: g('#e-note'),
      };
      DIMENSIONS.forEach((d) => {
        patch[`score_${d}`] = Number((container.querySelector(`#e-score-${d}`) as HTMLInputElement).value);
      });
      if ((container.querySelector('#e-reviewed') as HTMLInputElement).checked) {
        patch.last_reviewed_at = new Date().toISOString();
      }

      const updated = await updateRecord(record!.id, patch);
      Object.assign(record!, updated);
      editing = false;
      showToast('Saved');
      renderView();
    });
  }

  if (editing) { renderEdit(); } else { renderView(); }
}

function fieldRow(label: string, value: string, valueStyle = ''): string {
  return `<div style="display:flex; justify-content:space-between; gap:10px; padding:6px 0; font-size:13px; border-bottom:1px solid var(--border);">
    <span style="color:var(--text-faint); flex-shrink:0;">${escapeHtml(label)}</span>
    <span style="text-align:right; ${valueStyle}">${value}</span>
  </div>`;
}
